import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { useAuth, canManageOps } from "@/context/AuthContext";
import { BOLLA } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, FileDown, Pencil, Trash2 } from "lucide-react";
import { codiceBolla } from "@/lib/codici";
import BollaForm from "@/pages/bolle/BollaForm";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const CATEGORIE = [
  "Materiali",
  "Materiali edili",
  "Elettrici",
  "Idrici",
  "Condizionamento",
  "Porte",
  "Serramenti",
  "Impianti",
  "Finiture",
  "Ferramenta",
  "Attrezzature",
  "Trasporti",
  "Altro",
];

export default function Bolle() {
  const { user } = useAuth();
  // Chiunque loggato può inserire una bolla / DDT (anche il lavoratore che
  // riceve la merce sul cantiere). I prezzi restano visibili solo agli utenti
  // amministrativi tramite canSeePrices.
  const canCreate = !!user;
  const canSeePrices = user && ["amministratore", "impiegato_amministrativo", "responsabile_cantiere"].includes(user.role);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCantiere, setFilterCantiere] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const { data: bolle = [] } = useQuery({
    queryKey: ["bolle-all", filterCantiere],
    queryFn: () => api.get(`/bolle${filterCantiere ? `?cantiere_id=${filterCantiere}` : ""}`).then((r) => r.data),
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-all"],
    queryFn: () => api.get("/cantieri", { params: { tipo: "all" } }).then((r) => r.data),
  });

  const [form, setForm] = useState({
    numero_bolla: "",
    data: new Date().toISOString().slice(0, 10),
    fornitore: "",
    cantiere_id: "",
    materiale: "",
    quantita: 1,
    unita_misura: "pz",
    prezzo_unitario: 0,
    aliquota_iva: 22,
    categoria: "Materiali",
    numero_ordine: "",
    note: "",
    allegati: [],
    fornitore_partita_iva: "",
    fornitore_indirizzo: "",
  });

  // Upsert anagrafica fornitore (nuovo o esistente) — non blocca il flusso in caso di errore
  const upsertFornitore = async (f) => {
    if (!f.fornitore || (!f.fornitore_partita_iva && !f.fornitore_indirizzo)) return;
    try {
      await api.post("/entita/fornitori", {
        ragione_sociale: f.fornitore,
        partita_iva: f.fornitore_partita_iva || "",
        indirizzo: f.fornitore_indirizzo || "",
        categoria: f.categoria || "Materiali",
      });
    } catch (_) { /* silent */ }
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    for (const file of arr) {
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`"${file.name}" troppo grande (max 6 MB)`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      }).catch(() => null);
      if (b64) setForm((f) => ({ ...f, allegati: [...f.allegati, b64] }));
    }
  };

  const removeAllegato = (idx) =>
    setForm((f) => ({ ...f, allegati: f.allegati.filter((_, i) => i !== idx) }));

  const create = useMutation({
    mutationFn: async (payload) => {
      // Upsert anagrafica fornitore in parallelo (best-effort)
      upsertFornitore(payload);
      // Rimuovi campi ausiliari e converti i campi numerici (accetta virgola italiana)
      const { fornitore_partita_iva, fornitore_indirizzo, ...bollaPayload } = payload;
      const num = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        if (typeof v === "number") return v;
        const n = parseFloat(String(v).trim().replace(",", "."));
        return isNaN(n) ? 0 : n;
      };
      const clean = {
        ...bollaPayload,
        quantita: num(bollaPayload.quantita),
        prezzo_unitario: num(bollaPayload.prezzo_unitario),
        aliquota_iva: num(bollaPayload.aliquota_iva),
      };
      if (editingId) {
        return api.patch(`/bolle/${editingId}`, clean).then((r) => r.data);
      }
      return api.post("/bolle", clean).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(editingId ? "Bolla aggiornata" : "Bolla registrata");
      qc.invalidateQueries({ queryKey: ["bolle-all"] });
      qc.invalidateQueries({ queryKey: ["fornitori"] });
      setOpen(false);
      setEditingId(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const deleteBolla = useMutation({
    mutationFn: (bolla_id) => api.delete(`/bolle/${bolla_id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Bolla eliminata — spostata nel cestino, IVA ricalcolata");
      qc.invalidateQueries({ queryKey: ["bolle-all"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ"] });
    },
    onError: (e) => {
      const status = e?.response?.status ? `HTTP ${e.response.status} · ` : "";
      toast.error(`Eliminazione fallita — ${status}${e?.response?.data?.detail || e.message || "Errore"}`);
    },
  });

  const openNew = () => {
    setEditingId(null);
    setForm({
      numero_bolla: "",
      data: new Date().toISOString().slice(0, 10),
      fornitore: "",
      cantiere_id: "",
      materiale: "",
      quantita: "",
      unita_misura: "pz",
      prezzo_unitario: "",
      aliquota_iva: "22",
      categoria: "Materiali",
      numero_ordine: "",
      note: "",
      allegati: [],
      fornitore_partita_iva: "",
      fornitore_indirizzo: "",
    });
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditingId(b.bolla_id);
    setForm({
      numero_bolla: b.numero_bolla || "",
      data: b.data || new Date().toISOString().slice(0, 10),
      fornitore: b.fornitore || "",
      cantiere_id: b.cantiere_id || "",
      materiale: b.materiale || "",
      quantita: b.quantita != null ? String(b.quantita) : "",
      unita_misura: b.unita_misura || "pz",
      prezzo_unitario: b.prezzo_unitario != null ? String(b.prezzo_unitario) : "",
      aliquota_iva: b.aliquota_iva != null ? String(b.aliquota_iva) : "22",
      categoria: b.categoria || "Materiali",
      numero_ordine: b.numero_ordine || "",
      note: b.note || "",
      allegati: b.allegati || [],
    });
    setOpen(true);
  };

  // Parsa un numero da stringa accettando sia virgola che punto come separatore decimale.
  // Ritorna 0 se il valore è vuoto / non parsabile.
  const numIt = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return v;
    const cleaned = String(v).trim().replace(/\s/g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  };

  const tot = bolle.reduce((s, b) => s + b.imponibile, 0);
  const totIva = bolle.reduce((s, b) => s + b.importo_iva, 0);
  const totCon = bolle.reduce((s, b) => s + b.totale_con_iva, 0);

  const anteprima = {
    imponibile: (numIt(form.quantita) * numIt(form.prezzo_unitario)).toFixed(2),
    iva: ((numIt(form.quantita) * numIt(form.prezzo_unitario)) * (numIt(form.aliquota_iva) / 100)).toFixed(2),
    totale: ((numIt(form.quantita) * numIt(form.prezzo_unitario)) * (1 + numIt(form.aliquota_iva) / 100)).toFixed(2),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Bolle & Materiali</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Gestione bolle</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterCantiere} onChange={(e) => setFilterCantiere(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm">
            <option value="">Tutti (cantieri + manutenzioni)</option>
            {cantieri.filter((c) => c.tipo !== "manutenzione").map((c) => (
              <option key={c.cantiere_id} value={c.cantiere_id}>🏗️ {c.nome}</option>
            ))}
            {cantieri.filter((c) => c.tipo === "manutenzione").map((c) => (
              <option key={c.cantiere_id} value={c.cantiere_id}>🔧 {c.nome} (manut.)</option>
            ))}
          </select>
          <a href={`${backendUrl}/api/export/bolle/excel${filterCantiere?`?cantiere_id=${filterCantiere}`:""}`} className={`flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 ${canSeePrices ? "" : "hidden"}`}>
            <FileDown className="w-4 h-4" /> Excel
          </a>
          {canCreate && (
            <button
              onClick={openNew}
              data-testid={BOLLA.newButton}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm shadow-brutal"
            >
              <Plus className="w-4 h-4" /> Nuova bolla
            </button>
          )}
        </div>
      </div>

      {canSeePrices && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Bolle" value={bolle.length} />
          <StatCard label="Imponibile" value={eur(tot)} />
          <StatCard label="Totale IVA" value={eur(totIva)} />
          <StatCard label="Totale con IVA" value={eur(totCon)} highlight />
        </div>
      )}
      {!canSeePrices && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
          I prezzi delle bolle sono riservati ad amministratori e responsabili di cantiere.
          Vedi solo materiale, quantità e fornitore.
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">N°</th>
              <th className="text-left p-3">N° Ordine</th>
              <th className="text-left p-3">Fornitore</th>
              <th className="text-left p-3">Cantiere</th>
              <th className="text-left p-3">Materiale</th>
              <th className="text-left p-3">Cat.</th>
              <th className="text-left p-3">Codice</th>
              <th className="text-right p-3">Q.tà</th>
              {canSeePrices && <th className="text-right p-3">P.U.</th>}
              {canSeePrices && <th className="text-right p-3">Imponibile</th>}
              {canSeePrices && <th className="text-right p-3">IVA</th>}
              {canSeePrices && <th className="text-right p-3">Totale</th>}
              {canSeePrices && <th className="p-3"></th>}
            </tr>
          </thead>
          <tbody>
            {bolle.map((b) => {
              const needsPrice = (b.prezzo_unitario ?? 0) === 0 || (b.totale_con_iva ?? 0) === 0;
              return (
              <tr
                key={b.bolla_id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${
                  needsPrice && canSeePrices ? "bg-amber-50/40" : ""
                }`}
              >
                <td className="p-3 font-mono">{b.data}</td>
                <td className="p-3 font-mono text-xs">{b.numero_bolla}</td>
                <td className="p-3">
                  {b.numero_richiesta ? (
                    <span className="inline-block px-2 py-0.5 bg-orange-100 border border-orange-200 text-orange-800 font-mono text-[11px] font-bold rounded-sm" title="Numero richiesta materiali collegata">
                      {b.numero_richiesta}
                    </span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="p-3">{b.fornitore}</td>
                <td className="p-3">{b.cantiere_nome}</td>
                <td className="p-3">{b.materiale}</td>
                <td className="p-3 text-xs">{b.categoria}</td>
                <td className="p-3 font-mono font-bold text-orange-700 text-xs">{codiceBolla(b.categoria)}</td>
                <td className="p-3 text-right font-mono">{b.quantita} {b.unita_misura}</td>
                {canSeePrices && (
                  <td className="p-3 text-right font-mono">
                    {needsPrice ? (
                      <span className="text-amber-700 text-xs font-semibold">da inserire</span>
                    ) : (
                      eur(b.prezzo_unitario)
                    )}
                  </td>
                )}
                {canSeePrices && <td className="p-3 text-right font-mono">{eur(b.imponibile)}</td>}
                {canSeePrices && <td className="p-3 text-right font-mono text-slate-500">{eur(b.importo_iva)}</td>}
                {canSeePrices && <td className="p-3 text-right font-mono font-bold">{eur(b.totale_con_iva)}</td>}
                {canSeePrices && (
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => openEdit(b)}
                        data-testid={`btn-edit-bolla-${b.bolla_id}`}
                        title={needsPrice ? "Inserisci prezzo" : "Modifica bolla"}
                        className={`inline-flex items-center gap-1 text-xs font-semibold ${
                          needsPrice
                            ? "text-amber-700 hover:text-amber-900"
                            : "text-blue-700 hover:text-blue-900"
                        }`}
                      >
                        <Pencil className="w-3 h-3" />
                        {needsPrice ? "Aggiungi prezzo" : "Modifica"}
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(
                            `Eliminare la bolla "${b.numero_bolla || b.bolla_id}" del ${b.data}?\n\n` +
                            `Fornitore: ${b.fornitore}\n` +
                            `Verrà spostata nel cestino (ripristinabile) e l'IVA (previsionale/effettiva) verrà ricalcolata automaticamente.`
                          )) {
                            deleteBolla.mutate(b.bolla_id);
                          }
                        }}
                        disabled={deleteBolla.isPending}
                        data-testid={`btn-delete-bolla-${b.bolla_id}`}
                        title="Elimina bolla (sposta nel cestino)"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Elimina
                      </button>
                    </div>
                  </td>
                )}
              </tr>
              );
            })}
            {bolle.length === 0 && <tr><td colSpan={canSeePrices ? 13 : 8} className="p-8 text-center text-slate-400">Nessuna bolla</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <BollaForm
          editingId={editingId}
          form={form}
          setForm={setForm}
          cantieri={cantieri}
          anteprima={anteprima}
          codiceBolla={codiceBolla}
          onClose={() => { setOpen(false); setEditingId(null); }}
          onSubmit={(f) => create.mutateAsync(f)}
          saving={create.isPending}
          handleFiles={handleFiles}
          removeAllegato={removeAllegato}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}
